import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Phone, MessageSquare, UserRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { homeLabel, reporterHome } from "@/lib/home";
import { V } from "@/lib/vocab";

export const fmtPhone = (p) => {
  const d = (p || "").replace(/\D/g, "").replace(/^1(\d{10})$/, "$1");
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p || "";
};

// Who reported it — name, unit, number — with tap-to-call / tap-to-text. Shown on every event.
export function ReporterBlock({ ev, compact = false }) {
  const phone = ev.reporter_phone || ev.resident_phone || "";
  const name = ev.reporter_name || (ev.reporter_on_roster === false ? "Unknown reporter" : ev.resident_name || `${V.A("person")}`);
  const where = reporterHome(ev);
  return (
    <div data-testid="emergency-reporter" className={`rounded-xl border border-red-200 bg-white/70 ${compact ? "px-3 py-2" : "px-4 py-3"}`}>
      <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-red-700/70 flex items-center gap-1"><UserRound className="h-3 w-3" /> Reported by</div>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="font-bold text-ink">{name}</span>
        {ev.reporter_on_roster === false && <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold">not on roster — verify</span>}
        {where && <span className="text-ink/70">{where}</span>}
        {ev.location_note && <span className="text-ink/60 text-xs">· {ev.location_note}</span>}
      </div>
      {phone && (
        <div className="mt-1.5 flex flex-wrap gap-2">
          <a href={`tel:${phone}`} data-testid="emergency-reporter-call" className="inline-flex items-center gap-1.5 rounded-full bg-red-600 text-white px-3 py-1 text-xs font-bold hover:bg-red-700"><Phone className="h-3 w-3" /> Call {fmtPhone(phone)}</a>
          <a href={`sms:${phone}`} data-testid="emergency-reporter-text" className="inline-flex items-center gap-1.5 rounded-full border border-red-300 text-red-700 px-3 py-1 text-xs font-bold hover:bg-red-50"><MessageSquare className="h-3 w-3" /> Text</a>
        </div>
      )}
      {(ev.reporters || []).length > 0 && (
        <div className="mt-2 text-xs text-ink/70">
          Also reported by {ev.reporters.map((r) => `${r.name || fmtPhone(r.phone)}${homeLabel(r) ? ` (${homeLabel(r)})` : ""}`).join(", ")}
        </div>
      )}
    </div>
  );
}

const toLocalInput = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export const fmtDur = (mins) => {
  if (mins == null || Number.isNaN(mins)) return "—";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60), m = mins % 60;
  return m ? `${h} h ${String(m).padStart(2, "0")} min` : `${h} h`;
};

const AGO = [[15, "15 min ago"], [60, "1 h ago"], [180, "3 h ago"], [480, "8 h ago"]];

// Quick "N ago" picks that never land before the report.
export function AgoChips({ minIso, onPick, testid = "emergency-ago" }) {
  const lo = minIso ? new Date(minIso).getTime() : 0;
  return (
    <span className="inline-flex flex-wrap gap-1.5">
      {AGO.map(([mins, label]) => {
        const t = Date.now() - mins * 60000;
        if (t <= lo) return null;
        return (
          <button key={mins} type="button" data-testid={`${testid}-${mins}`} onClick={() => onPick(toLocalInput(new Date(t)))}
            className="rounded-full border border-sand-300 px-2.5 py-0.5 text-[11px] font-semibold text-ink/70 hover:border-ink hover:text-ink">{label}</button>
        );
      })}
    </span>
  );
}

// "When was it actually over?" — the honest time for the record when someone marks it late.
export function ResolvedWhen({ earlier, setEarlier, when, setWhen, minIso }) {
  const now = toLocalInput(new Date());
  const min = minIso ? toLocalInput(new Date(minIso)) : undefined;
  const onRecord = minIso ? Math.round(((earlier && when ? new Date(when) : new Date()).getTime() - new Date(minIso).getTime()) / 60000) : null;
  const pick = (v) => { setEarlier(true); setWhen(v); };
  return (
    <div data-testid="emergency-resolved-when" className="rounded-xl border border-sand-200 p-3 text-sm">
      <div className="text-xs font-bold uppercase tracking-wide text-ink/45">When was it actually resolved?</div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button type="button" data-testid="emergency-resolved-now" onClick={() => setEarlier(false)}
          className={`rounded-full px-3 py-1 text-xs font-semibold ${!earlier ? "bg-ink text-white" : "border border-sand-300 text-ink"}`}>Just now</button>
        <button type="button" data-testid="emergency-resolved-earlier" onClick={() => { setEarlier(true); if (!when) setWhen(now); }}
          className={`rounded-full px-3 py-1 text-xs font-semibold ${earlier ? "bg-ink text-white" : "border border-sand-300 text-ink"}`}>Earlier…</button>
        <AgoChips minIso={minIso} onPick={pick} />
      </div>
      {earlier && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input data-testid="emergency-resolved-at" type="datetime-local" value={when} min={min} max={now} onChange={(e) => setWhen(e.target.value)}
            className="rounded-lg border border-sand-200 px-2 py-1 text-xs outline-none focus:border-ink" />
          <span className="text-[11px] text-ink/50">The record keeps both: when it was over, and that you marked it now.</span>
        </div>
      )}
      {onRecord != null && onRecord >= 0 && (
        <p data-testid="emergency-resolved-on-record" className="mt-1.5 text-[11px] text-ink/50">
          Time to resolve on the record: <span className="font-bold text-ink/70">{fmtDur(onRecord)}</span>
        </p>
      )}
    </div>
  );
}

// Resolve from the dashboard. The all-clear text is shown in full and is NOT sent unless ticked.
export function ResolveDialog({ open, onClose, tenantId, ev, onResolved }) {
  const [note, setNote] = useState("");
  const [sendAllClear, setSendAllClear] = useState(false);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [earlier, setEarlier] = useState(false);
  const [when, setWhen] = useState("");
  useEffect(() => {
    if (!open) return;
    setNote(""); setSendAllClear(false); setEarlier(false); setWhen("");
    api.get(`/tenants/${tenantId}/emergency/all-clear-preview`).then((r) => setPreview(r.data.text)).catch(() => setPreview(""));
  }, [open, tenantId]);
  const go = async () => {
    if (earlier && !when) { toast.error("Pick when it was actually resolved"); return; }
    setBusy(true);
    try {
      await api.post(`/tenants/${tenantId}/emergency/events/${ev.id}/resolve`, {
        notify_resident: sendAllClear, note, resolved_at: earlier && when ? new Date(when).toISOString() : null });
      toast.success(sendAllClear ? "Resolved — all-clear sent to the reporter" : `Resolved — nothing sent to the ${V.lc("person")}`);
      onResolved?.(); onClose();
    } catch (e) { toast.error(errMsg(e, "Couldn't resolve")); }
    finally { setBusy(false); }
  };
  const who = ev?.reporter_name || `the ${V.lc("person")}`;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="emergency-resolve-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Mark this emergency resolved</DialogTitle>
          <DialogDescription>Closes the event{ev?.ticket_ref ? ` and request ${ev.ticket_ref}` : ""}. Nothing is texted to anyone unless you tick the box below.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <textarea data-testid="emergency-resolve-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Resolution note (optional) — what happened, who fixed it"
            className="w-full rounded-xl border border-sand-200 px-3 py-2 text-sm outline-none focus:border-ink" />
          <ResolvedWhen earlier={earlier} setEarlier={setEarlier} when={when} setWhen={setWhen} minIso={ev?.created_at} />
          <label className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer ${sendAllClear ? "border-emerald-300 bg-emerald-50" : "border-sand-200"}`}>
            <input data-testid="emergency-resolve-send-allclear" type="checkbox" checked={sendAllClear} onChange={(e) => setSendAllClear(e.target.checked)} className="mt-1 h-4 w-4 accent-emerald-600" />
            <span className="text-sm">
              <span className="font-semibold text-ink">Text {who} the all-clear</span>
              <span className="block text-xs text-ink/60 mt-1">Exactly this, nothing else:</span>
              <span data-testid="emergency-resolve-allclear-text" className="block mt-1 rounded-lg bg-white border border-sand-200 px-2.5 py-1.5 text-xs text-ink/80 italic">“{preview || "…"}”</span>
            </span>
          </label>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-full px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-sand-100">Cancel</button>
          <button data-testid="emergency-resolve-confirm" onClick={go} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {sendAllClear ? `Resolve and text the ${V.lc("person")}` : "Resolve (no text)"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
